/**
 * Shared hook for managing pending interactive block state with dismiss support.
 * Used by ChatBox (via ChatTranscript) and ChatPage (standalone ChatTranscript).
 */
import { useState, useCallback } from 'react';
import type { PendingInteractiveBlock } from './ChatTranscript';

interface UsePendingBlockDismissOptions {
  /** Called when the pending block changes (after filtering dismissed blocks) */
  onPendingBlockUpdate?: (pending: PendingInteractiveBlock | null) => void;
}

export function usePendingBlockDismiss(options: UsePendingBlockDismissOptions = {}) {
  const { onPendingBlockUpdate } = options;
  const [pendingBlock, setPendingBlock] = useState<PendingInteractiveBlock | null>(null);
  const [dismissedBlockKey, setDismissedBlockKey] = useState<string | null>(null);

  const handlePendingBlockUpdate = useCallback((pending: PendingInteractiveBlock | null) => {
    // If this block was dismissed, don't show it
    if (pending && dismissedBlockKey === `${pending.block.question}:${pending.blockIndex}`) {
      setPendingBlock(null);
      return;
    }
    // New block detected — clear any stale dismissal
    if (pending && dismissedBlockKey) {
      setDismissedBlockKey(null);
    }
    setPendingBlock(pending);
    onPendingBlockUpdate?.(pending);
  }, [onPendingBlockUpdate, dismissedBlockKey]);

  const handleDismissPendingBlock = useCallback(() => {
    if (pendingBlock) {
      setDismissedBlockKey(`${pendingBlock.block.question}:${pendingBlock.blockIndex}`);
    }
    setPendingBlock(null);
    onPendingBlockUpdate?.(null);
  }, [onPendingBlockUpdate, pendingBlock]);

  return {
    pendingBlock,
    handlePendingBlockUpdate,
    handleDismissPendingBlock,
  };
}
